import { useState, useEffect } from 'react';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Transaction, Goal, Debt, IncomeStream, RecurringExpense, WishlistItem, MonthlyReport, BankAccount, PlannedPayment, PaymentFile, DismissedOccurrence, BankStatement } from '../types';

export function useBudgetData(householdId: string | null) {
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [goals, setGoals] = useState<Goal[]>([]);
  const [debts, setDebts] = useState<Debt[]>([]);
  const [incomes, setIncomes] = useState<IncomeStream[]>([]);
  const [recurringExpenses, setRecurringExpenses] = useState<RecurringExpense[]>([]);
  const [wishlist, setWishlist] = useState<WishlistItem[]>([]);
  const [reports, setReports] = useState<MonthlyReport[]>([]);
  const [plannedPayments, setPlannedPayments] = useState<PlannedPayment[]>([]);
  const [paymentFiles, setPaymentFiles] = useState<PaymentFile[]>([]);
  const [dismissedOccurrences, setDismissedOccurrences] = useState<DismissedOccurrence[]>([]);
  const [bankStatements, setBankStatements] = useState<BankStatement[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!householdId) return;

    let loadedCount = 0;
    const TOTAL_COLLECTIONS = 12;
    const checkLoaded = () => {
      loadedCount++;
      if (loadedCount >= TOTAL_COLLECTIONS) setLoading(false);
    };

    const parseDate = (d: any) => {
      if (!d) return null;
      if (typeof d.toDate === 'function') return d.toDate();
      if (d instanceof Date) return isNaN(d.getTime()) ? null : d;
      const parsed = new Date(d);
      return isNaN(parsed.getTime()) ? null : parsed;
    };

    const unsubTransactions = onSnapshot(
      query(collection(db, 'transactions'), where('householdId', '==', householdId)), 
      (snapshot) => {
        const txs = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(), 
          date: parseDate(doc.data().date) || new Date()
        })) as Transaction[];
        txs.sort((a, b) => b.date.getTime() - a.date.getTime());
        setTransactions(txs);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading transactions:", error);
        checkLoaded();
      }
    );

    const unsubBankAccounts = onSnapshot(
      query(collection(db, 'bank_accounts'), where('householdId', '==', householdId)), 
      (snapshot) => {
        const ba = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(), 
          createdAt: parseDate(doc.data().createdAt) || new Date()
        })) as BankAccount[];
        ba.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setBankAccounts(ba);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading bank accounts:", error);
        checkLoaded();
      }
    );

    const unsubGoals = onSnapshot(
      query(collection(db, 'goals'), where('householdId', '==', householdId)), 
      (snapshot) => {
        const g = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(), 
          deadline: parseDate(doc.data().deadline),
          createdAt: parseDate(doc.data().createdAt) || new Date()
        })) as Goal[];
        g.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setGoals(g);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading goals:", error);
        checkLoaded();
      }
    );

    const unsubDebts = onSnapshot(
      query(collection(db, 'debts'), where('householdId', '==', householdId)), 
      (snapshot) => {
        const d = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(), 
          schedule: doc.data().schedule ? {
            ...doc.data().schedule,
            nextDate: parseDate(doc.data().schedule.nextDate)
          } : undefined,
          createdAt: parseDate(doc.data().createdAt) || new Date()
        })) as Debt[];
        d.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setDebts(d);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading debts:", error);
        checkLoaded();
      }
    );

    const unsubIncomes = onSnapshot(
      query(collection(db, 'incomes'), where('householdId', '==', householdId)), 
      (snapshot) => {
        const inc = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(), 
          schedule: doc.data().schedule ? {
            ...doc.data().schedule,
            nextDate: parseDate(doc.data().schedule.nextDate)
          } : undefined,
          createdAt: parseDate(doc.data().createdAt) || new Date()
        })) as IncomeStream[];
        inc.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setIncomes(inc);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading incomes:", error);
        checkLoaded();
      }
    );

    const unsubExpenses = onSnapshot(
      query(collection(db, 'recurring_expenses'), where('householdId', '==', householdId)), 
      (snapshot) => {
        const exp = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(), 
          schedule: doc.data().schedule ? {
            ...doc.data().schedule,
            nextDate: parseDate(doc.data().schedule.nextDate)
          } : undefined,
          createdAt: parseDate(doc.data().createdAt) || new Date()
        })) as RecurringExpense[];
        exp.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setRecurringExpenses(exp);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading recurring expenses:", error);
        checkLoaded();
      }
    );

    const unsubWishlist = onSnapshot(
      query(collection(db, 'wishlist'), where('householdId', '==', householdId)), 
      (snapshot) => {
        const w = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(), 
          targetDate: parseDate(doc.data().targetDate),
          createdAt: parseDate(doc.data().createdAt) || new Date()
        })) as WishlistItem[];
        w.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setWishlist(w);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading wishlist:", error);
        checkLoaded();
      }
    );

    const unsubReports = onSnapshot(
      query(collection(db, 'monthly_reports'), where('householdId', '==', householdId)), 
      (snapshot) => {
        const r = snapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data(), 
          createdAt: parseDate(doc.data().createdAt) || new Date()
        })) as MonthlyReport[];
        r.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setReports(r);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading monthly reports:", error);
        checkLoaded();
      }
    );

    const unsubPlannedPayments = onSnapshot(
      query(collection(db, 'planned_payments'), where('householdId', '==', householdId)),
      (snapshot) => {
        const pp = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          date: parseDate(doc.data().date),
          createdAt: parseDate(doc.data().createdAt) || new Date()
        })) as PlannedPayment[];
        pp.sort((a, b) => a.date.getTime() - b.date.getTime()); // Sort by date ascending
        setPlannedPayments(pp);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading planned payments:", error);
        checkLoaded();
      }
    );

    const unsubPaymentFiles = onSnapshot(
      query(collection(db, 'payment_files'), where('householdId', '==', householdId)),
      (snapshot) => {
        const pf = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: parseDate(doc.data().createdAt) || new Date()
        })) as PaymentFile[];
        setPaymentFiles(pf);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading payment files:", error);
        checkLoaded();
      }
    );

    const unsubDismissedOccurrences = onSnapshot(
      query(collection(db, 'dismissed_occurrences'), where('householdId', '==', householdId)),
      (snapshot) => {
        const dismissed = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          createdAt: parseDate(doc.data().createdAt) || new Date()
        })) as DismissedOccurrence[];
        setDismissedOccurrences(dismissed);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading dismissed occurrences:", error);
        checkLoaded();
      }
    );

    const unsubBankStatements = onSnapshot(
      query(collection(db, 'bank_statements'), where('householdId', '==', householdId)),
      (snapshot) => {
        const bs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data(),
          statementPeriodStart: parseDate(doc.data().statementPeriodStart),
          statementPeriodEnd: parseDate(doc.data().statementPeriodEnd),
          createdAt: parseDate(doc.data().createdAt) || new Date()
        })) as BankStatement[];
        bs.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
        setBankStatements(bs);
        checkLoaded();
      },
      (error) => {
        console.error("Error loading bank statements:", error);
        checkLoaded();
      }
    );

    return () => {
      unsubBankAccounts();
      unsubTransactions();
      unsubGoals();
      unsubDebts();
      unsubIncomes();
      unsubExpenses();
      unsubWishlist();
      unsubReports();
      unsubPlannedPayments();
      unsubPaymentFiles();
      unsubDismissedOccurrences();
      unsubBankStatements();
    };
  }, [householdId]);

  return { bankAccounts, transactions, goals, debts, incomes, recurringExpenses, wishlist, reports, plannedPayments, paymentFiles, dismissedOccurrences, bankStatements, loading };
}
